import { Trash2 } from "lucide-react";
import type { FacetOp, OpType } from "../facet-form-config";
import { OP_TYPES } from "../facet-form-config";
import { defaultOpForType } from "../facet-form-helpers";
import FacetFormField, { INPUT_CLASS } from "./FacetFormField";
import FacetFormDropdown from "./FacetFormDropdown";
import FacetFormToggle from "./FacetFormToggle";
import FacetFormMultiInput from "./FacetFormMultiInput";
import FacetFormMultiSelect from "./FacetFormMultiSelect";
import TransformEditor from "./TransformEditor";
import type { TransformAxis } from "../facet-transform-apply-helpers";
import NormalizeOpFields from "./NormalizeOpFields";
import type { SourceListItem } from "../../../tars-hooks/useSourceList";
import type { SourceNode } from "../../sidebar/sources-types";

const OP_ACCENT: Record<string, string> = {
    filter: "border-l-amber-400",
    pivot: "border-l-indigo-400",
    normalize: "border-l-emerald-400",
};

const OP_BADGE: Record<string, string> = {
    filter: "bg-amber-50 text-amber-700",
    pivot: "bg-indigo-50 text-indigo-700",
    normalize: "bg-emerald-50 text-emerald-700",
};

interface FacetOpSectionProps {
    op: FacetOp;
    index: number;
    getRowValueOptions: (rowKeyHint: string) => string[];
    getNormalizeKeyValueOptions: (keyHint: string) => string[];
    columnOptions: string[];
    referenceTableOptions: string[];
    dataBankSources: SourceListItem[] | undefined;
    tarsNodes: Record<string, SourceNode>;
    errors?: Record<string, string>;
    onChange: (index: number, op: FacetOp) => void;
    onRemove: (index: number) => void;
    onApplyTransform?: (opIndex: number, axis: TransformAxis) => void;
    onNormalizeKeyCommit?: (opIndex: number, key: string) => void;
    isApplyingTransform?: boolean;
    isLoadingNormalizeKeyValues?: boolean;
    canApplyTransform?: boolean;
    optionsRefreshKey?: number;
}

export default function FacetOpSection({
    op,
    index,
    getRowValueOptions,
    getNormalizeKeyValueOptions,
    columnOptions,
    referenceTableOptions,
    dataBankSources,
    tarsNodes,
    errors,
    onChange,
    onRemove,
    onApplyTransform,
    onNormalizeKeyCommit,
    isApplyingTransform = false,
    isLoadingNormalizeKeyValues = false,
    canApplyTransform = true,
    optionsRefreshKey = 0,
}: FacetOpSectionProps) {
    const patch = (updates: Partial<FacetOp>) => onChange(index, { ...op, ...updates });
    const patchType = (nextType: OpType) => {
        if (nextType === op.type) return;
        onChange(index, defaultOpForType(nextType));
    };
    const isPivot = op.type === "pivot";
    const isNormalize = op.type === "normalize";
    const rowKey = (op.row_key ?? "").trim();
    const colKey = (op.col_key ?? "").trim();
    const rowTransformOptions = rowKey ? getRowValueOptions(rowKey) : [];
    const colTransformOptions = colKey ? columnOptions : [];

    return (
        <div
            className={`rounded-xl border border-l-[3px] border-slate-200 bg-white shadow-sm ${OP_ACCENT[op.type] ?? "border-l-slate-300"}`}
        >
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                    <span className="text-[12px] font-bold text-slate-700">Op {index + 1}</span>
                    <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${OP_BADGE[op.type] ?? "bg-slate-100 text-slate-600"}`}
                    >
                        {op.type}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={() => onRemove(index)}
                    className="rounded-md p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                    title="Remove operation"
                >
                    <Trash2 size={14} />
                </button>
            </div>

            <div className="flex flex-col gap-4 p-4">
                {op.type === "filter" && (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                        <FacetFormField label="Type">
                            <FacetFormDropdown
                                value={op.type}
                                options={[...OP_TYPES]}
                                onChange={(v) => patchType(v as OpType)}
                            />
                        </FacetFormField>
                        <FacetFormField label="Key" error={errors?.key}>
                            <input
                                type="text"
                                value={op.key ?? ""}
                                onChange={(e) => patch({ key: e.target.value })}
                                placeholder="e.g. qa_status"
                                className={INPUT_CLASS}
                            />
                        </FacetFormField>
                        <FacetFormField label="Blacklist">
                            <FacetFormMultiInput
                                values={op.blacklist ?? []}
                                onChange={(v) => patch({ blacklist: v })}
                            />
                        </FacetFormField>
                    </div>
                )}

                {isPivot && (
                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                            <FacetFormField label="Type">
                                <FacetFormDropdown
                                    value={op.type}
                                    options={[...OP_TYPES]}
                                    onChange={(v) => patchType(v as OpType)}
                                />
                            </FacetFormField>
                            <FacetFormField label="Row Key" error={errors?.row_key}>
                                <input
                                    type="text"
                                    value={op.row_key ?? ""}
                                    onChange={(e) => patch({ row_key: e.target.value })}
                                    placeholder="e.g. profile_age"
                                    className={INPUT_CLASS}
                                />
                            </FacetFormField>
                            <FacetFormField label="Col Key">
                                <input
                                    type="text"
                                    value={op.col_key ?? ""}
                                    onChange={(e) => patch({ col_key: e.target.value })}
                                    placeholder="Optional"
                                    className={INPUT_CLASS}
                                />
                            </FacetFormField>
                        </div>

                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col gap-3 rounded-lg border border-indigo-200/90 bg-indigo-50/70 p-3.5 pt-3">
                                <FacetFormField label="Row Transform">
                                    <TransformEditor
                                        entries={op.row_transform ?? [{ key: "", value: "" }]}
                                        options={rowTransformOptions}
                                        onChange={(v) => patch({ row_transform: v })}
                                        onApplyTransform={
                                            onApplyTransform
                                                ? () => onApplyTransform(index, "row")
                                                : undefined
                                        }
                                        isApplying={isApplyingTransform}
                                        canApplyTransform={canApplyTransform}
                                        optionsRefreshKey={optionsRefreshKey}
                                    />
                                </FacetFormField>
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <FacetFormField label="Row Blacklist">
                                        <FacetFormMultiSelect
                                            values={op.row_blacklist ?? []}
                                            options={rowTransformOptions}
                                            onChange={(v) => patch({ row_blacklist: v })}
                                            optionsRefreshKey={optionsRefreshKey}
                                        />
                                    </FacetFormField>
                                    <FacetFormField label="Row Whitelist">
                                        <FacetFormMultiSelect
                                            values={op.row_whitelist ?? []}
                                            options={rowTransformOptions}
                                            onChange={(v) => patch({ row_whitelist: v })}
                                            optionsRefreshKey={optionsRefreshKey}
                                        />
                                    </FacetFormField>
                                </div>
                                <div className="mt-1 flex justify-start border-t border-indigo-200/60 pt-3">
                                    <FacetFormToggle
                                        label="Include Total Rows"
                                        checked={op.include_total_rows ?? true}
                                        onChange={(v) => patch({ include_total_rows: v })}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col gap-3 rounded-lg border border-teal-200/90 bg-teal-50/55 p-3.5 pt-3">
                                <FacetFormField label="Col Transform">
                                    <TransformEditor
                                        entries={op.col_transform ?? [{ key: "", value: "" }]}
                                        options={colTransformOptions}
                                        onChange={(v) => patch({ col_transform: v })}
                                        onApplyTransform={
                                            onApplyTransform
                                                ? () => onApplyTransform(index, "col")
                                                : undefined
                                        }
                                        isApplying={isApplyingTransform}
                                        canApplyTransform={canApplyTransform}
                                        optionsRefreshKey={optionsRefreshKey}
                                    />
                                </FacetFormField>
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <FacetFormField label="Col Blacklist">
                                        <FacetFormMultiSelect
                                            values={op.col_blacklist ?? []}
                                            options={colTransformOptions}
                                            onChange={(v) => patch({ col_blacklist: v })}
                                            optionsRefreshKey={optionsRefreshKey}
                                        />
                                    </FacetFormField>
                                    <FacetFormField label="Col Whitelist">
                                        <FacetFormMultiSelect
                                            values={op.col_whitelist ?? []}
                                            options={colTransformOptions}
                                            onChange={(v) => patch({ col_whitelist: v })}
                                            optionsRefreshKey={optionsRefreshKey}
                                        />
                                    </FacetFormField>
                                </div>
                                <div className="mt-1 flex justify-start border-t border-teal-200/60 pt-3">
                                    <FacetFormToggle
                                        label="Include Total Cols"
                                        checked={op.include_total_cols ?? true}
                                        onChange={(v) => patch({ include_total_cols: v })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {isNormalize && (
                    <NormalizeOpFields
                        op={op}
                        errors={errors}
                        referenceTableOptions={referenceTableOptions}
                        getNormalizeKeyValueOptions={getNormalizeKeyValueOptions}
                        optionsRefreshKey={optionsRefreshKey}
                        dataBankSources={dataBankSources}
                        tarsNodes={tarsNodes}
                        onPatch={patch}
                        onTypeChange={patchType}
                        onNormalizeKeyCommit={
                            onNormalizeKeyCommit
                                ? (key) => onNormalizeKeyCommit(index, key)
                                : undefined
                        }
                        isLoadingKeyValues={isLoadingNormalizeKeyValues}
                    />
                )}
            </div>
        </div>
    );
}
